function readNormalized(raw: unknown): string {
  const s = raw != null ? String(raw).trim() : "";
  return s ? s.replace(/\/$/, "") : "";
}

function stageKeyUpper() {
  const mode = String(import.meta.env.MODE || "").toLowerCase();
  if (mode === "development" || mode === "dev") return "DEVELOPMENT";
  if (mode === "staging" || mode === "stage") return "STAGING";
  if (mode === "production" || mode === "prod") return "PRODUCTION";
  return "";
}

function stageAliases() {
  const key = stageKeyUpper();
  if (!key) return [];
  if (key === "DEVELOPMENT") return ["DEVELOPMENT", "DEV"];
  if (key === "STAGING") return ["STAGING", "STAGE"];
  if (key === "PRODUCTION") return ["PRODUCTION", "PROD"];
  return [key];
}

function firstNonEmpty(values: unknown[]): string {
  for (const v of values) {
    const s = readNormalized(v);
    if (s) return s;
  }
  return "";
}

/**
 * Separate dashboard SPA origin (no trailing slash).
 * Supports a global var and stage-specific vars:
 * - VITE_DASHBOARD_ORIGIN
 * - VITE_DASHBOARD_ORIGIN_DEV / _STAGING / _PRODUCTION
 */
export function getDashboardOrigin() {
  const staged = stageAliases()
    .map((k) => import.meta.env[`VITE_DASHBOARD_ORIGIN_${k}`])
    .filter(Boolean);
  const resolved = firstNonEmpty([import.meta.env.VITE_DASHBOARD_ORIGIN, ...staged]);
  if (resolved) return resolved;

  if (import.meta.env.DEV) return "http://localhost:3001";
  return "";
}

function getDashboardEntryFromEnv() {
  const staged = stageAliases()
    .map((k) => import.meta.env[`VITE_DASHBOARD_ENTRY_URL_${k}`])
    .filter(Boolean);
  return firstNonEmpty([import.meta.env.VITE_DASHBOARD_ENTRY_URL, ...staged]);
}

/** Full URL to open the dashboard home. */
export function getDashboardEntryUrl() {
  const entry = getDashboardEntryFromEnv();
  if (entry) return entry;
  const o = getDashboardOrigin();
  if (o) return `${o}/`;
  return "/";
}

/** Callback URL used to hand over auth tokens after login. */
export function getDashboardCallbackUrl() {
  const o = getDashboardOrigin();
  if (o) return `${o}/auth/callback`;

  const entry = getDashboardEntryUrl();
  if (/^https?:\/\//i.test(entry)) {
    try {
      return new URL("/auth/callback", entry).toString().replace(/\/$/, "");
    } catch {
      // Fall through to same-origin callback.
    }
  }
  return "/auth/callback";
}

/** Absolute URL to a path on the dashboard SPA (login, register, etc.). */
export function getDashboardPathUrl(pathname: string): string {
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const o = getDashboardOrigin();
  if (o) return `${o}${p}`;
  if (import.meta.env.DEV) return `http://localhost:3001${p}`;
  return p;
}

export function getDashboardLoginUrl() {
  return getDashboardPathUrl("/login");
}

export function getDashboardRegisterUrl() {
  return getDashboardPathUrl("/register");
}
